// Global Instructions Rule Applied!
//
// Company short descriptions (2026-11-03) on Configuration → Follower
// Agents, beside Backfill: the agent writes the short "what the company
// does" that reports print, for every portfolio company that has none, from
// the company's description and its brief overview. Admins only (the routes
// refuse anyone else).
//
// Generate asks the backend for one batch at a time (cfg batch_size) and
// keeps asking until nothing is left, a batch writes nothing, or Stop; each
// batch is safe to repeat, so closing the tab only pauses the work. The
// tiles say how the portfolio stands; the tabs list what needs a person:
// texts waiting for a sign-off, companies with nothing to write from, and
// companies the agent declined. Chrome: the Queue section's tiles, a
// FormSection, antd Tables under a height cap.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Table, Tabs, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchShortDescriptionsStatus,
  generateShortDescriptions
} from '../../companies/services/shortDescriptionService.ts'
import type {
  CompanyRef,
  CompanyShortDescriptionsStatus,
  ShortDescriptionsBatch
} from '../../../state_manager/data_models/company_short_description.ts'

const { Text } = Typography

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
const LIST_SCROLL = { y: 320 }

type WrittenRow = CompanyRef & { accounts__short_description: string | null }

interface RunTotals {
  written: WrittenRow[]
  declined: CompanyRef[]
  failed: CompanyRef[]
  costUsd: number
}

const EMPTY_RUN: RunTotals = { written: [], declined: [], failed: [], costUsd: 0 }

export function CompanyShortDescriptionsSection() {
  const { openFormInTab } = useTabManagerContext()
  const [status, setStatus] = useState<CompanyShortDescriptionsStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [includeDeclined, setIncludeDeclined] = useState(false)
  const [run, setRun] = useState<RunTotals | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState('unreviewed')
  const stopRef = useRef(false)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    const res = await fetchShortDescriptionsStatus(getAccessToken(store.getState().auth))
    setLoading(false)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'The short descriptions status could not be loaded.')
      return
    }
    setStatus(res.data)
  }, [])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const handleGenerate = useCallback(async () => {
    stopRef.current = false
    setRunning(true)
    setActiveTab('run')
    let totals: RunTotals = EMPTY_RUN
    setRun(totals)
    try {
      for (;;) {
        const res = await generateShortDescriptions(includeDeclined, getAccessToken(store.getState().auth))
        if (res.error || !res.data) {
          showErrorToast(res.error || 'The agent could not write the short descriptions.')
          break
        }
        const batch: ShortDescriptionsBatch = res.data
        totals = {
          written: [...totals.written, ...batch.written],
          declined: [...totals.declined, ...batch.declined],
          failed: [...totals.failed, ...batch.failed],
          costUsd: totals.costUsd + batch.cost_usd
        }
        setRun(totals)
        setRemaining(batch.remaining)
        const progressed = batch.written.length + batch.declined.length + batch.superseded > 0
        // a retry of the declined ones goes round once: what it declines
        // again stays eligible and would come back forever
        if (batch.remaining === 0 || !progressed || stopRef.current || includeDeclined) break
      }
      if (totals.written.length > 0) toast.success(`${totals.written.length} short descriptions written`)
    } finally {
      setRunning(false)
      void loadStatus()
    }
  }, [includeDeclined, loadStatus])

  const companyLink = useCallback(
    (row: CompanyRef) => (
      <button
        type='button'
        className={RECORD_LINK_CLASS}
        onClick={() => openFormInTab('companies', row.accounts__id, row.accounts__name || 'Company')}
      >
        {row.accounts__name || 'Company'}
      </button>
    ),
    [openFormInTab]
  )

  const companyColumn = { title: 'Company', key: 'company', width: 260, render: (_: unknown, row: CompanyRef) => companyLink(row) }
  const reasonColumns: ColumnsType<CompanyRef> = [
    companyColumn,
    { title: 'Why', key: 'reason', render: (_: unknown, row) => row.prog__reason || '—' }
  ]
  const writtenColumns: ColumnsType<WrittenRow> = [
    companyColumn,
    { title: 'Short description', key: 'text', render: (_: unknown, row) => row.accounts__short_description }
  ]

  const tile = (label: string, value: React.ReactNode, hint?: string) => (
    <Tooltip title={hint}>
      <div>
        <span className='block text-[10.5px] tracking-wider uppercase text-gray-400'>{label}</span>
        <span className='text-base font-semibold tabular-nums'>{value}</span>
      </div>
    </Tooltip>
  )

  const table = <T extends CompanyRef>(rows: T[], columns: ColumnsType<T>, empty: string) =>
    rows.length === 0 ? (
      <Text type='secondary'>{empty}</Text>
    ) : (
      <Table<T>
        size='small'
        pagination={false}
        rowKey='accounts__id'
        columns={columns}
        dataSource={rows}
        scroll={LIST_SCROLL}
        tableLayout='auto'
      />
    )

  const headerActions = running ? (
    <div className='flex items-center gap-2'>
      <Text type='secondary' className='text-xs'>
        Writing… {run?.written.length ?? 0} written{remaining != null ? `, ${remaining} to go` : ''}
      </Text>
      <Button size='small' onClick={() => (stopRef.current = true)}>
        Stop
      </Button>
    </div>
  ) : (
    <div className='flex items-center gap-2'>
      <Tooltip title='Ask the agent again about the companies it declined last time (say, after their description was filled in).'>
        <Checkbox checked={includeDeclined} onChange={(e) => setIncludeDeclined(e.target.checked)}>
          <span className='text-xs'>Retry declined</span>
        </Checkbox>
      </Tooltip>
      <Button
        size='small'
        type='primary'
        disabled={!status || (status.to_generate === 0 && !(includeDeclined && status.declined_companies.length > 0))}
        onClick={() => void handleGenerate()}
      >
        Generate missing
      </Button>
    </div>
  )

  return (
    <FormSection
      title='Company short descriptions'
      titleColor='purple'
      loading={loading && !status}
      className='shadow-sm border border-gray-200'
      headerActions={headerActions}
    >
      <p className='text-gray-500 text-xs mt-0 mb-2'>
        The one or two sentences on what a company does that the Investor Performance Report, the Quarterly Fund Report
        and the SPV report print (in place of the CRM description). The agent writes one for every portfolio company that
        has none, from its description and its company brief overview only: a company with neither is skipped, and one
        whose sources do not say what it does is declined. Its texts print at once and read “agent-written” until a person
        signs them off (or edits them) on the company’s Details tab; reports warn in their preview until then.
      </p>
      {status ? (
        <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5 mb-3'>
          {tile('Portfolio', status.portfolio_total, 'Every company a fund holds or held.')}
          {tile('By a person', status.written, 'Written or edited by a person.')}
          {tile('Signed off', status.signed_off, 'Agent-written and signed off.')}
          {tile('To sign off', status.unreviewed, 'Agent-written and not yet signed off: reports print them and warn in their preview.')}
          {tile('To generate', status.to_generate, 'No short description yet, and a description or brief overview to write it from.')}
          {tile('Nothing to go on', status.no_source_companies.length, 'Neither a description nor a company brief overview: a person writes these.')}
          {tile('Declined', status.declined_companies.length, 'The agent found its sources did not say what the company does.')}
          {tile(
            'Spent today',
            `$${(status.cost_today_usd ?? 0).toFixed(2)}`,
            'Model spend on short descriptions since midnight UTC (cfg company_short_descriptions / max_cost_per_day_usd caps it).'
          )}
        </div>
      ) : null}
      {status ? (
        <Tabs
          size='small'
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            ...(run
              ? [
                  {
                    key: 'run',
                    label: `This run (${run.written.length} written${run.declined.length ? `, ${run.declined.length} declined` : ''}${run.failed.length ? `, ${run.failed.length} failed` : ''})`,
                    children: (
                      <div className='space-y-3'>
                        {table(run.written, writtenColumns, running ? 'Writing the first batch…' : 'Nothing was written.')}
                        {run.declined.length || run.failed.length
                          ? table([...run.declined, ...run.failed], reasonColumns, '')
                          : null}
                        <Text type='secondary' className='text-xs'>
                          Spent ${run.costUsd.toFixed(2)}.
                        </Text>
                      </div>
                    )
                  }
                ]
              : []),
            {
              key: 'unreviewed',
              label: `To sign off (${status.unreviewed_companies.length})`,
              children: table(status.unreviewed_companies, [companyColumn], 'Every agent-written text is signed off.')
            },
            {
              key: 'no_source',
              label: `Nothing to go on (${status.no_source_companies.length})`,
              children: table(status.no_source_companies, [companyColumn], 'Every portfolio company has a source to write from.')
            },
            {
              key: 'declined',
              label: `Declined (${status.declined_companies.length})`,
              children: table(status.declined_companies, reasonColumns, 'The agent has declined none.')
            }
          ]}
        />
      ) : null}
    </FormSection>
  )
}

export default CompanyShortDescriptionsSection
